import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, Sparkles, CheckCircle2, Clock, AlertCircle,
  Layers, X, Search, ChevronDown, ChevronRight, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { projectAPI } from '../lib/api';

const statusOptions = [
  { value: 'planned', label: 'Planned', color: 'text-gray-400 bg-gray-500/10 border-gray-500/20' },
  { value: 'in_development', label: 'In Development', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  { value: 'completed', label: 'Completed', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
  { value: 'released', label: 'Released', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
];

const categoryOptions = ['Core', 'Enhancement', 'Integration', 'UI/UX', 'API', 'Security', 'Performance'];

export default function NewFeaturesPanel({ projectId, canEdit = true }) {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedModule, setExpandedModule] = useState(null);
  const [newFeature, setNewFeature] = useState({
    module: '',
    name: '',
    description: '',
    category: 'Core',
    status: 'planned',
    priority: 'Medium',
    estimated_effort: '',
    dependencies: '',
  });

  useEffect(() => {
    loadFeatures();
  }, [projectId]);

  const loadFeatures = async () => {
    try {
      const res = await projectAPI.getNewFeatures(projectId);
      setFeatures(res.data || []);
    } catch (err) {
      console.log('Loading features:', err);
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeature = async () => {
    if (!newFeature.name.trim()) {
      toast.error('Feature name is required');
      return;
    }
    try {
      await projectAPI.addNewFeature(projectId, newFeature);
      setShowAddModal(false);
      setNewFeature({
        module: '', name: '', description: '', category: 'Core',
        status: 'planned', priority: 'Medium', estimated_effort: '', dependencies: '',
      });
      await loadFeatures();
      toast.success('Feature added');
    } catch (err) {
      toast.error('Failed to add feature');
    }
  };

  const handleUpdateStatus = async (featureId, newStatus) => {
    try {
      await projectAPI.updateNewFeature(projectId, featureId, { status: newStatus });
      await loadFeatures();
      toast.success('Status updated');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (featureId) => {
    try {
      await projectAPI.deleteNewFeature(projectId, featureId);
      await loadFeatures();
      toast.success('Deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Group features by module
  const groupedFeatures = features.reduce((acc, feature) => {
    const module = feature.module || 'Uncategorized';
    if (!acc[module]) acc[module] = [];
    acc[module].push(feature);
    return acc;
  }, {});

  const filteredGrouped = Object.entries(groupedFeatures).reduce((acc, [module, moduleFeatures]) => {
    const filtered = moduleFeatures.filter(f => {
      const matchesSearch = f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            f.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || f.status === filterStatus;
      const matchesCategory = filterCategory === 'all' || f.category === filterCategory;
      return matchesSearch && matchesStatus && matchesCategory;
    });
    if (filtered.length > 0) acc[module] = filtered;
    return acc;
  }, {});

  const getStatusBadge = (status) => {
    const opt = statusOptions.find(s => s.value === status) || statusOptions[0];
    return <Badge className={opt.color}>{opt.label}</Badge>;
  };

  const stats = {
    total: features.length,
    planned: features.filter(f => f.status === 'planned').length,
    inDev: features.filter(f => f.status === 'in_development').length,
    completed: features.filter(f => f.status === 'completed' || f.status === 'released').length,
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="h-full flex flex-col" data-testid="new-features-panel">
      {/* Header */}
      <div className="p-4 border-b border-[var(--surface-border)]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-left">New Features</h2>
            <p className="text-xs opacity-60 text-left">Track modules and feature development</p>
          </div>
          {canEdit && (
            <Button size="sm" onClick={() => setShowAddModal(true)}>
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Feature
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="p-2 rounded-lg bg-[var(--surface-card)] border border-[var(--surface-border)] text-center">
            <div className="text-lg font-semibold">{stats.total}</div>
            <div className="text-[10px] opacity-50">Total</div>
          </div>
          <div className="p-2 rounded-lg bg-gray-500/10 border border-gray-500/20 text-center">
            <div className="text-lg font-semibold text-gray-400">{stats.planned}</div>
            <div className="text-[10px] text-gray-400">Planned</div>
          </div>
          <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
            <div className="text-lg font-semibold text-amber-400">{stats.inDev}</div>
            <div className="text-[10px] text-amber-400">In Dev</div>
          </div>
          <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
            <div className="text-lg font-semibold text-emerald-400">{stats.completed}</div>
            <div className="text-[10px] text-emerald-400">Done</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-40" />
            <Input
              placeholder="Search features..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px] h-8">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {statusOptions.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[130px] h-8">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categoryOptions.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {Object.keys(filteredGrouped).length === 0 ? (
            <div className="text-center py-12 opacity-60">
              <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No features found</p>
              {canEdit && <p className="text-xs mt-1">Click "Add Feature" to track new development</p>}
            </div>
          ) : (
            Object.entries(filteredGrouped).map(([module, moduleFeatures]) => (
              <div key={module} className="rounded-lg border border-[var(--surface-border)] bg-[var(--surface-card)] overflow-hidden">
                {/* Module Header */}
                <div
                  className="p-3 cursor-pointer hover:bg-[var(--surface-bg)]/30 flex items-center justify-between"
                  onClick={() => setExpandedModule(expandedModule === module ? null : module)}
                >
                  <div className="flex items-center gap-2">
                    {expandedModule === module ? <ChevronDown className="w-4 h-4 opacity-50" /> : <ChevronRight className="w-4 h-4 opacity-50" />}
                    <Layers className="w-4 h-4 text-[var(--accent-indigo)]" />
                    <span className="font-medium">{module}</span>
                    <Badge variant="outline" className="text-[10px]">{moduleFeatures.length} features</Badge>
                  </div>
                  <div className="flex gap-1">
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">
                      {moduleFeatures.filter(f => f.status === 'completed' || f.status === 'released').length} done
                    </Badge>
                  </div>
                </div>

                {/* Features List */}
                {expandedModule === module && (
                  <div className="border-t border-[var(--surface-border)]">
                    {moduleFeatures.map((feature, index) => (
                      <div
                        key={feature.id}
                        className={`p-3 ${index < moduleFeatures.length - 1 ? 'border-b border-[var(--surface-border)]' : ''} hover:bg-[var(--surface-bg)]/20`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {getStatusBadge(feature.status)}
                              <Badge variant="outline" className="text-[10px]">{feature.category}</Badge>
                              {feature.priority === 'High' && (
                                <Badge variant="outline" className="text-[10px] text-red-400 border-red-500/30">High Priority</Badge>
                              )}
                            </div>
                            <h4 className="font-medium text-left">{feature.name}</h4>
                            {feature.description && (
                              <p className="text-sm opacity-60 mt-1 text-left">{feature.description}</p>
                            )}
                            {feature.estimated_effort && (
                              <p className="text-xs opacity-40 mt-1 text-left">Effort: {feature.estimated_effort}</p>
                            )}
                          </div>
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleDelete(feature.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex gap-1 mt-2">
                            {statusOptions.map(s => (
                              <Button
                                key={s.value}
                                variant={feature.status === s.value ? 'default' : 'ghost'}
                                size="sm"
                                className="text-[10px] h-6 px-2"
                                onClick={() => handleUpdateStatus(feature.id, s.value)}
                              >
                                {s.label}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-card)] rounded-xl border border-[var(--surface-border)] w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add New Feature</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Module</label>
                <Input value={newFeature.module} onChange={(e) => setNewFeature({ ...newFeature, module: e.target.value })} placeholder="e.g., Authentication, Dashboard, API" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Feature Name *</label>
                <Input value={newFeature.name} onChange={(e) => setNewFeature({ ...newFeature, name: e.target.value })} placeholder="Feature name" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Description</label>
                <Textarea value={newFeature.description} onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })} placeholder="Feature description" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Category</label>
                  <Select value={newFeature.category} onValueChange={(v) => setNewFeature({ ...newFeature, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Priority</label>
                  <Select value={newFeature.priority} onValueChange={(v) => setNewFeature({ ...newFeature, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Estimated Effort</label>
                <Input value={newFeature.estimated_effort} onChange={(e) => setNewFeature({ ...newFeature, estimated_effort: e.target.value })} placeholder="e.g., 2 days, 1 sprint" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Dependencies</label>
                <Input value={newFeature.dependencies} onChange={(e) => setNewFeature({ ...newFeature, dependencies: e.target.value })} placeholder="Other features or modules this depends on" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleAddFeature}>Add Feature</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
